// utils/employeeExcel.js - 社友 Excel 處理工具
import * as XLSX from 'xlsx';
import axios from './axiosConfig';

// 下載社友 Excel 範本或現有資料
export const downloadEmployeesExcel = async (token, isTemplate = false) => {
  try {
    console.log('開始下載社友 Excel...', isTemplate ? '（範本模式）' : '（完整資料）');

    const endpoint = isTemplate ? '/employees/export-template' : '/employees/export';
    const response = await axios.get(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob',
    });

    console.log('收到回應:', response.status);

    // 檢查回應是否為 Excel 檔案
    const contentType = response.headers['content-type'];
    if (!contentType || !contentType.includes('spreadsheetml')) {
      if (contentType && contentType.includes('application/json')) {
        try {
          const reader = new FileReader();
          const errorData = await new Promise((resolve, reject) => {
            reader.onload = () => {
              try {
                const result = JSON.parse(reader.result);
                resolve(result);
              } catch (e) {
                reject(e);
              }
            };
            reader.onerror = reject;
            reader.readAsText(response.data);
          });
          throw new Error(errorData.message || errorData.error || '伺服器返回錯誤');
        } catch (parseError) {
          throw new Error('伺服器返回錯誤，但無法解析錯誤訊息');
        }
      }
      throw new Error(`回應不是 Excel 檔案格式，收到的格式：${contentType}`);
    }

    // 建立 Blob 並下載
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // 設定檔名
    let fileName = isTemplate
      ? `社友匯入範本_${new Date().toISOString().slice(0, 10)}.xlsx`
      : `社友資料_${new Date().toISOString().slice(0, 10)}.xlsx`;

    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
      if (utf8Match) {
        fileName = decodeURIComponent(utf8Match[1]);
      } else {
        const regularMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (regularMatch && regularMatch[1]) {
          fileName = regularMatch[1].replace(/['"]/g, '');
        }
      }
    }

    console.log('下載檔名:', fileName);

    link.setAttribute('download', fileName);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    // 清理
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);

    return { success: true, message: '社友資料下載成功！' };
  } catch (error) {
    console.error('下載社友 Excel 失敗:', error);

    let errorMessage = '下載失敗，請稍後再試';
    if (error.response?.status === 403) {
      errorMessage = '權限不足：只有最高權限（e_id=80）才能下載社友資料';
    } else if (error.response?.status === 401) {
      errorMessage = '請重新登入後再試';
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = `下載失敗: ${error.message}`;
    }

    return { success: false, message: errorMessage };
  }
};

// 處理 Excel 檔案上傳並解析
export const handleExcelFileUpload = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('請選擇要上傳的檔案'));
      return;
    }

    // 檢查檔案類型
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
    ];

    if (!validTypes.includes(file.type)) {
      reject(new Error('請選擇 Excel 檔案 (.xlsx 或 .xls)'));
      return;
    }

    // 檢查檔案大小 (限制 10MB)
    if (file.size > 10 * 1024 * 1024) {
      reject(new Error('檔案大小不能超過 10MB'));
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // 取得第一個工作表
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // 轉換為 JSON 格式
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          reject(new Error('Excel 檔案沒有資料'));
          return;
        }

        // 驗證必要欄位（根據實際需求調整）
        const requiredColumns = ['ID', '姓名'];
        const firstRow = jsonData[0];
        const missingColumns = requiredColumns.filter(col => !(col in firstRow));

        if (missingColumns.length > 0) {
          reject(new Error(`缺少必要欄位：${missingColumns.join(', ')}\n\n請確認您使用的是正確的匯入範本`));
          return;
        }

        resolve({
          success: true,
          data: jsonData,
          message: `成功讀取 ${jsonData.length} 筆社友資料`
        });

      } catch (error) {
        console.error('解析 Excel 檔案失敗:', error);
        reject(new Error('Excel 檔案格式錯誤，請檢查檔案內容'));
      }
    };

    reader.onerror = () => {
      reject(new Error('讀取檔案失敗'));
    };

    reader.readAsArrayBuffer(file);
  });
};

// 預覽匯入資料的變更
export const previewEmployeeChanges = async (excelData, token) => {
  try {
    const response = await axios.post('/employees/preview-import', {
      excelData
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return {
      success: true,
      data: response.data,
      message: response.data.message || '預覽資料載入成功'
    };

  } catch (error) {
    console.error('預覽資料失敗:', error);

    let errorMessage = '預覽失敗，請稍後再試';
    if (error.response?.status === 403) {
      errorMessage = '權限不足：只有最高權限（e_id=80）才能匯入社友資料';
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }

    return {
      success: false,
      message: errorMessage,
      error: error.response?.data || error.message
    };
  }
};

// 上傳社友資料到後端
export const uploadEmployeesData = async (excelData, token) => {
  try {
    const response = await axios.post('/employees/import', {
      excelData
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return {
      success: true,
      data: response.data,
      message: response.data.message || '匯入成功！'
    };

  } catch (error) {
    console.error('上傳社友資料失敗:', error);

    let errorMessage = '上傳失敗，請稍後再試';
    if (error.response?.status === 403) {
      errorMessage = '權限不足：只有最高權限（e_id=80）才能匯入社友資料';
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }

    return {
      success: false,
      message: errorMessage,
      error: error.response?.data || error.message
    };
  }
};

// 格式化匯入結果訊息
export const formatImportResults = (results) => {
  // 支援 { data: { results: {...} } } 或 { results: {...} } 結構
  const data = results?.data?.results || results?.results;
  if (!data) {
    return results?.data?.message || results?.message || '匯入完成';
  }

  const { success, updated, created, failed, errors } = data;
  let message = `匯入完成：`;

  if (created > 0) {
    message += `新增 ${created} 筆`;
  }

  if (updated > 0) {
    if (created > 0) message += '，';
    message += `更新 ${updated} 筆`;
  }

  if (success > 0 && created === 0 && updated === 0) {
    message += `成功 ${success} 筆`;
  }

  if (failed > 0) {
    message += `，失敗 ${failed} 筆`;

    if (errors && errors.length > 0) {
      message += '\n\n失敗項目：\n';
      errors.slice(0, 5).forEach(error => {
        message += `• 第${error.row}行 (${error.name || error.employee}): ${error.error}\n`;
      });

      if (errors.length > 5) {
        message += `• 還有 ${errors.length - 5} 個錯誤...`;
      }
    }
  }

  return message;
};

// 格式化預覽變更資訊
export const formatPreviewChanges = (previewData) => {
  if (!previewData || !previewData.changes) {
    return { total: 0, summary: '沒有資料變更' };
  }

  const { toUpdate, toCreate, unchanged, errors } = previewData.changes;

  const total = (toUpdate?.length || 0) + (toCreate?.length || 0);

  let summary = [];

  if (toCreate && toCreate.length > 0) {
    summary.push(`新增 ${toCreate.length} 筆`);
  }

  if (toUpdate && toUpdate.length > 0) {
    summary.push(`更新 ${toUpdate.length} 筆`);
  }

  if (unchanged && unchanged.length > 0) {
    summary.push(`無變更 ${unchanged.length} 筆`);
  }

  if (errors && errors.length > 0) {
    summary.push(`錯誤 ${errors.length} 筆`);
  }

  return {
    total,
    summary: summary.join('，') || '沒有資料變更',
    details: previewData.changes
  };
};
